import { Component, OnInit, ChangeDetectionStrategy, output, inject, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { BRAND_NAME, MenuGroup, MenuItem, filterMenuForUser } from '../../../constants/app-menu';

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="sn-root">
      <!-- Brand -->
      <div class="sn-brand">
        <div class="sn-brand-inner" (click)="goHome()">
          <div class="sn-logo-fallback"><img src="logo.svg" alt="Vinayaka Jewellers" /></div>
          <div class="sn-brand-text">
            <span class="sn-brand-name">{{ brandName }}</span>
            <span class="sn-brand-role">{{ userRole || 'Admin Panel' }}</span>
          </div>
        </div>
        <button type="button" class="sn-close-btn" (click)="toggleSidebar.emit()" title="Close menu">
          <i class="pi pi-bars"></i>
        </button>
      </div>

      <!-- Menu -->
      <nav class="sn-menu">
        <!-- Top-level Dashboard / Home link -->
        <a
          class="sn-item sn-item-top"
          [routerLink]="['/home']"
          routerLinkActive="active"
          [routerLinkActiveOptions]="{ exact: true }"
          (click)="navigated.emit()">
          <i class="pi pi-home sn-item-icon"></i>
          <span>Dashboard</span>
        </a>

        <!-- Accordion groups -->
        @for (group of groups; track trackByCategory($index, group)) {
          <div class="sn-group">
            <button type="button" class="sn-group-head" (click)="toggleGroup(group.category)">
              <span class="sn-group-icon" [style.background]="group.iconBg" [style.color]="group.iconColor">
                <i [class]="group.icon"></i>
              </span>
              <span class="sn-group-label">{{ group.category }}</span>
              <i class="pi sn-chevron" [ngClass]="isExpanded(group.category) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
            </button>
            @if (isExpanded(group.category)) {
              <div class="sn-items">
                @for (item of group.items; track trackByRoute($index, item)) {
                  <a
                    class="sn-item"
                    [class.active]="activeRoute() === item.route"
                    [routerLink]="['/' + item.route]"
                    (click)="navigated.emit()">
                    <i [class]="item.icon" class="sn-item-icon"></i>
                    <span>{{ item.label }}</span>
                  </a>
                }
              </div>
            }
          </div>
        }
      </nav>
    </div>
    `,
  styles: [`
    .sn-root { display:flex; flex-direction:column; height:100%; background:#fff; }
    .sn-brand {
      display:flex; align-items:center; padding:0 8px 0 16px;
      border-bottom:1px solid #eef0f3; flex-shrink:0; height:56px;
    }
    .sn-brand-inner {
      display:flex; align-items:center; gap:10px; flex:1; min-width:0;
      cursor:pointer; overflow:hidden;
    }
    .sn-close-btn {
      width:36px; height:36px; flex-shrink:0; border:none; background:transparent;
      border-radius:8px; cursor:pointer; color:#94a3b8;
      display:flex; align-items:center; justify-content:center; font-size:16px;
    }
    .sn-close-btn:hover { background:#f1f5f9; color:#334155; }
    .sn-logo-fallback {
      width:36px; height:36px; border-radius:10px; flex-shrink:0;
      background:transparent;
      display:flex; align-items:center; justify-content:center;
      box-shadow:0 4px 10px rgba(217,119,6,0.3);
    }
    .sn-logo-fallback img { width:100%; height:100%; display:block; border-radius:inherit; }
    .sn-brand-text { display:flex; flex-direction:column; min-width:0; }
    .sn-brand-name { font-size:14px; font-weight:700; color:#1e293b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .sn-brand-role { font-size:11px; color:#94a3b8; }

    .sn-menu { flex:1; overflow-y:auto; padding:8px; }
    .sn-group { margin-bottom:2px; }
    .sn-group-head {
      width:100%; display:flex; align-items:center; gap:10px;
      padding:9px 10px; border:none; background:transparent; cursor:pointer;
      border-radius:9px; text-align:left; color:#334155;
    }
    .sn-group-head:hover { background:#f1f5f9; }
    .sn-group-icon {
      width:26px; height:26px; border-radius:7px; flex-shrink:0;
      display:flex; align-items:center; justify-content:center; font-size:13px;
    }
    .sn-group-label { flex:1; font-size:13px; font-weight:600; }
    .sn-chevron { font-size:11px; color:#94a3b8; }

    .sn-items { padding:2px 0 6px 12px; display:flex; flex-direction:column; }
    .sn-item {
      display:flex; align-items:center; gap:10px;
      padding:8px 10px 8px 14px; margin:1px 0;
      border-radius:8px; font-size:13px; color:#475569;
      text-decoration:none; border-left:2px solid transparent;
    }
    .sn-item-top {
      margin:0 0 6px; padding:9px 10px; font-weight:600; color:#334155;
      border-left:none;
    }
    .sn-item:hover { background:#f8fafc; color:#1e293b; }
    .sn-item-icon { font-size:13px; width:16px; text-align:center; color:#94a3b8; }
    .sn-item.active {
      background:#fffbeb; color:#b45309; font-weight:600;
      border-left-color:#d97706;
    }
    .sn-item-top.active { border-left:none; }
    .sn-item.active .sn-item-icon { color:#d97706; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideNavComponent implements OnInit {
  readonly navigated = output<void>();
  readonly toggleSidebar = output<void>();

  readonly brandName = BRAND_NAME;

  private expanded = new Set<string>();

  public authService = inject(AuthService);
  private router = inject(Router);

  // Current URL as a signal, updated on every completed navigation. Drives the
  // active-item highlight under OnPush without relying on the RouterLinkActive directive.
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Route of the single active menu item — longest matching prefix wins, so
  // `products/add` beats `products` at /products/add, while `products` still wins
  // at /products/edit/9 (keeps the parent list item highlighted on detail/edit pages).
  readonly activeRoute = computed<string | null>(() => {
    const url = this.currentUrl().split(/[?#]/)[0].replace(/^\//, '');
    let best: string | null = null;
    for (const g of this.groups) {
      for (const it of g.items) {
        if (url === it.route || url.startsWith(it.route + '/')) {
          if (best === null || it.route.length > best.length) best = it.route;
        }
      }
    }
    return best;
  });

  /**
   * Always-fresh filtered menu groups. Getter (not a field) so every
   * change-detection cycle re-evaluates against the current role/power-user —
   * reflects permission changes without a manual refresh.
   */
  get groups(): MenuGroup[] {
    return filterMenuForUser(this.authService.getUserRole(), this.authService.isPowerUser());
  }

  ngOnInit() {
    // Auto-expand the section that owns the current route.
    const url = this.router.url.replace(/^\//, '');
    const current = this.groups.find(g =>
      g.items.some(it => url === it.route || url.startsWith(it.route + '/'))
    );
    if (current) this.expanded.add(current.category);
    else if (this.groups.length) this.expanded.add(this.groups[0].category);
  }

  get userRole(): string { return this.authService.getUserRole(); }

  toggleGroup(cat: string) {
    this.expanded.has(cat) ? this.expanded.delete(cat) : this.expanded.add(cat);
  }
  isExpanded(cat: string): boolean { return this.expanded.has(cat); }

  goHome() {
    this.router.navigate(['/home']);
    this.navigated.emit();
  }

  // trackBy stops Angular from destroying/recreating DOM nodes when the
  // `groups` getter returns a freshly-built array on every change-detection cycle.
  trackByCategory = (_i: number, g: MenuGroup) => g?.category;
  trackByRoute = (_i: number, it: MenuItem) => it?.route;
}
